import { motion } from "framer-motion";
import { ArrowRight, Phone, ExternalLink } from "lucide-react";
import Seo from "../components/Seo";
import { infotech } from "../data/infotech";

const { company, capabilities, projects } = infotech;
const liveCount = projects.reduce((n, p) => n + 1 + p.modules.length, 0);

// SVG pulse line from the original design brief
function PulseLine() {
  return (
    <svg
      viewBox="0 0 640 220"
      className="h-full w-full"
      preserveAspectRatio="none"
      role="img"
      aria-label="A pulse line running across the panel"
    >
      <defs>
        <linearGradient id="pulseFade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#1FBFB8" stopOpacity="0.15" />
          <stop offset="0.35" stopColor="#1FBFB8" stopOpacity="1" />
          <stop offset="1" stopColor="#7FE3DE" stopOpacity="0.9" />
        </linearGradient>
      </defs>
      <g stroke="#0F4E74" strokeWidth="1" opacity="0.55">
        {[44, 88, 132, 176].map((y) => (
          <line key={y} x1="0" y1={y} x2="640" y2={y} />
        ))}
      </g>
      <path
        d="M0 132 H92 L118 132 L136 74 L158 172 L176 110 L196 132 H286 L306 132 L322 96 L340 150 L356 132 H436 L458 132 L474 52 L498 186 L516 118 L534 132 H640"
        fill="none"
        stroke="url(#pulseFade)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: 1400,
          strokeDashoffset: 1400,
          animation: "software-draw 2.4s ease-out 0.15s forwards",
        }}
      />
    </svg>
  );
}

function TagPill({ label }) {
  return (
    <span className="inline-flex items-center rounded-full border border-[#0F4E74]/15 bg-white px-2.5 py-1 text-[10.5px] font-mono uppercase tracking-wider text-[#0F4E74]">
      {label}
    </span>
  );
}

export default function Software() {
  return (
    <div data-testid="software-page" className="relative bg-[#F7F5EF] text-[#0E1B26]">
      <Seo
        title="Hayat Infotech — Custom Web Systems, Jamaat & CCTV Software"
        description="Hayat Infotech is the software arm of Hayat — building jamaat management, event operations, live streaming and business websites used in production today. Call +91 96738 78652."
        canonicalPath="/software"
      />

      {/* Blueprint background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,78,116,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(15,78,116,0.08) 1px, transparent 1px)",
          backgroundSize: "42px 42px",
        }}
      />

      {/* ---------- Hero ---------- */}
      <section
        id="top"
        data-testid="software-hero"
        className="relative border-b border-slate-200"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-14 pb-20 pt-16 md:pb-24 md:pt-24 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-16 relative">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-[11.5px] font-mono uppercase tracking-[0.18em] text-[#0F4E74]">
              {company.tagline}
            </p>

            <h1 className="mt-5 text-[34px] font-bold leading-[1.08] text-[#0E1B26] sm:text-[42px] lg:text-[48px]">
              We build the systems that keep{" "}
              <span className="text-[#0F4E74]">communities</span> and{" "}
              <span className="text-[#1FBFB8]">businesses</span> running.
            </h1>

            <p className="mt-6 max-w-[54ch] text-[17.5px] leading-relaxed text-slate-600 sm:text-[19px]">
              Hayat Infotech designs and builds custom web platforms — jamaat and event
              management, CCTV operations, live streaming and business websites.
              Everything below is live in production today.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href="#work"
                data-testid="software-cta-work"
                className="inline-flex items-center gap-2.5 rounded-full bg-[#0E1B26] px-6 py-3.5 text-[15px] font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5"
              >
                See the work <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={`tel:${company.phoneDial}`}
                data-testid="software-cta-call"
                className="inline-flex items-center gap-2.5 rounded-full border border-[#0F4E74]/25 bg-white px-6 py-3.5 text-[15px] font-semibold text-[#0F4E74] transition-colors hover:border-[#0F4E74]/60"
              >
                <Phone className="w-4 h-4" /> {company.phoneDisplay}
              </a>
            </div>

            <div className="mt-11 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-slate-200 pt-7">
              <p className="flex items-baseline gap-2.5">
                <span className="font-display text-[28px] font-bold leading-none tabular-nums text-[#0E1B26]">
                  {liveCount}
                </span>
                <span className="text-[11.5px] font-mono uppercase tracking-widest text-slate-500">
                  products live
                </span>
              </p>
              <ul className="flex flex-wrap gap-2">
                {["Community", "Security", "Events", "Trade"].map((sector) => (
                  <li key={sector}>
                    <TagPill label={sector} />
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[26px] bg-[#0A2A3B] p-7 shadow-[0_30px_60px_-25px_rgba(15,78,116,0.6)] sm:p-9">
              <div className="absolute inset-x-0 top-1/2 h-[220px] -translate-y-1/2 opacity-90">
                <PulseLine />
              </div>
              <div className="relative flex h-[300px] flex-col justify-between sm:h-[330px]">
                <p className="text-[11.5px] font-mono uppercase tracking-[0.18em] text-[#7FE3DE]">
                  حياة · ḥayāt · life
                </p>
                <div>
                  <p className="font-display text-[26px] font-semibold leading-snug text-white sm:text-[30px]">
                    A system is only good
                    <br />
                    while it is running.
                  </p>
                  <p className="mt-4 max-w-[36ch] text-[15.5px] leading-relaxed text-white/70">
                    Seven domains, nine products, in daily use by jamaats, event teams and
                    businesses.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- Capabilities ---------- */}
      <section
        id="capabilities"
        data-testid="software-capabilities"
        className="relative border-b border-slate-200"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-[11.5px] font-mono uppercase tracking-[0.18em] text-[#0F4E74]">
            What we build
          </p>
          <h2 className="mt-3 max-w-2xl text-3xl md:text-4xl font-bold tracking-tight text-[#0E1B26]">
            Four practice areas. Every product below fits inside one.
          </h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {capabilities.map((c, i) => (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                data-testid={`software-capability-${c.key}`}
                className="relative rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0F4E74]/40 hover:shadow-md transition-all"
              >
                <span className="absolute top-5 right-5 font-mono text-[10.5px] text-slate-400">
                  0{i + 1}
                </span>
                <h3 className="font-display text-lg font-semibold text-[#0E1B26]">
                  {c.title}
                </h3>
                <p className="mt-2.5 text-[14.5px] leading-relaxed text-slate-600">
                  {c.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Work ---------- */}
      <section id="work" data-testid="software-work" className="relative border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-[11.5px] font-mono uppercase tracking-[0.18em] text-[#0F4E74]">
                In production today
              </p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-[#0E1B26]">
                Seven domains. Nine products.
              </h2>
            </div>
            <p className="text-slate-500 text-sm max-w-md">
              Each link opens the live production site. If a project has modules, they run
              inside it and are listed underneath.
            </p>
          </div>

          <div className="mt-10 space-y-4">
            {projects.map((p, idx) => (
              <motion.article
                key={p.domain}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (idx % 6) * 0.04 }}
                data-testid={`software-project-${p.domain}`}
                className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-[#0F4E74]/40 transition-colors"
              >
                <a
                  href={p.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="group block p-6 md:p-7"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h3 className="font-display text-xl md:text-2xl font-bold text-[#0E1B26] group-hover:text-[#0F4E74] transition-colors">
                          {p.name}
                        </h3>
                        <TagPill label={p.tag} />
                      </div>
                      <p className="text-[13px] font-mono text-slate-500 mt-1">
                        {p.domain}
                      </p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#0F4E74] shrink-0 mt-1.5 transition-colors" />
                  </div>
                  <p className="mt-4 text-[15.5px] font-semibold text-[#0F4E74]">
                    {p.tagline}
                  </p>
                  <p className="mt-1.5 text-[14.5px] leading-relaxed text-slate-600 max-w-3xl">
                    {p.body}
                  </p>
                </a>

                {p.modules.length > 0 && (
                  <div className="border-t border-slate-100 bg-slate-50/60 px-6 md:px-7 py-4">
                    <p className="text-[11.5px] font-mono uppercase tracking-widest text-slate-400 mb-3">
                      Modules inside {p.name}
                    </p>
                    <div className="grid gap-3 md:grid-cols-2">
                      {p.modules.map((m) => (
                        <a
                          key={m.domain}
                          href={m.href}
                          target="_blank"
                          rel="noreferrer noopener"
                          data-testid={`software-module-${m.domain.replace(/\W+/g, "-")}`}
                          className="group flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 hover:border-[#0F4E74]/50 transition-colors"
                        >
                          <div className="min-w-0">
                            <div className="font-display font-semibold text-[#0E1B26] group-hover:text-[#0F4E74] transition-colors">
                              {m.name}
                            </div>
                            <div className="text-[12px] font-mono text-slate-500 mt-0.5 truncate">
                              {m.domain}
                            </div>
                            <p className="text-[13.5px] text-slate-600 mt-1.5">
                              {m.body}
                            </p>
                          </div>
                          <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#0F4E74] shrink-0 mt-1 transition-colors" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="rounded-3xl bg-gradient-to-br from-[#0A2A3B] to-[#0F4E74] p-8 md:p-14 text-white relative overflow-hidden">
            <div className="absolute -right-8 top-0 h-full w-1/2 opacity-30">
              <PulseLine />
            </div>
            <div className="relative max-w-2xl">
              <p className="text-[11.5px] font-mono uppercase tracking-[0.18em] text-[#7FE3DE]">
                Talk to Hayat Infotech
              </p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight">
                Have a system that should have been built years ago?
              </h2>
              <p className="mt-4 text-white/70 text-[15.5px] leading-relaxed">
                We take on a small number of projects each quarter — jamaat, event,
                security or trade. Send a note and we&apos;ll reply the same day.
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                <a
                  href={`tel:${company.phoneDial}`}
                  data-testid="software-final-call"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-[#0F4E74] px-6 py-3.5 text-sm font-semibold hover:bg-slate-100 transition-colors"
                >
                  <Phone className="w-4 h-4" /> {company.phoneDisplay}
                </a>
                <a
                  href={company.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="software-final-whatsapp"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 text-sm font-semibold hover:bg-[#1eb355] transition-colors"
                >
                  WhatsApp us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes software-draw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}
